<script lang="ts">
  import { onMount } from "svelte";
  import { api } from "../lib/api";
  import {
    loadSettings,
    saveSettings,
    listModels,
    normalizeBaseUrl,
    type AiSettings,
  } from "../lib/ai";

  interface Props {
    onClose: () => void;
    onSaved?: () => void;
  }

  let { onClose, onSaved }: Props = $props();

  let form = $state<AiSettings>(loadSettings());
  let serverProvider = $state<{ enabled: boolean; model: string } | null>(null);
  let checking = $state(false);
  let checkResult = $state<string | null>(null);
  let checkError = $state<string | null>(null);
  let diagnosing = $state(false);
  let directResult = $state<string | null>(null);
  let serverResult = $state<string | null>(null);

  onMount(async () => {
    try {
      serverProvider = await api.aiConfig();
    } catch {
      serverProvider = null;
    }
  });

  /**
   * Проверяет оба пути: прямой из браузера и через сервер.
   * Это главный инструмент, когда «чат не отвечает».
   */
  async function diagnose() {
    diagnosing = true;
    directResult = null;
    serverResult = null;

    if (form.baseUrl.trim() === "") {
      directResult = "не задан base url — режим «свой провайдер» выключен";
    } else {
      try {
        const models = await listModels(form);
        directResult = `браузер → провайдер: ok, моделей ${models.length}`;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        directResult =
          `браузер → провайдер: ошибка (${msg}). ` +
          "Обычно это CORS или Chrome Local Network Access (публичный сайт не может " +
          "обращаться к приватному адресу).";
      }
    }

    try {
      const res = await api.aiPing();
      serverResult = res.ok
        ? `сервер → провайдер: ok, модель ${res.model ?? form.model}`
        : `сервер → провайдер: ошибка [${res.stage}${res.status ? " " + res.status : ""}] ${res.message ?? ""}`;
    } catch (e) {
      serverResult = `сервер → провайдер: ошибка запроса (${e instanceof Error ? e.message : String(e)})`;
    }

    diagnosing = false;
  }

  async function check() {
    checking = true;
    checkResult = null;
    checkError = null;
    try {
      const models = await listModels(form);
      checkResult =
        models.length > 0
          ? `ok · ${models.length} моделей, например: ${models.slice(0, 3).join(", ")}`
          : "ok · ответ получен, но список моделей пуст";
    } catch (e) {
      checkError = e instanceof Error ? e.message : String(e);
    } finally {
      checking = false;
    }
  }

  function save() {
    saveSettings({
      ...form,
      baseUrl: normalizeBaseUrl(form.baseUrl),
      model: form.model.trim(),
      apiKey: form.apiKey.trim(),
    });
    onSaved?.();
    onClose();
  }
</script>

<div class="backdrop" role="presentation" onclick={(e) => e.target === e.currentTarget && onClose()}>
  <div class="dialog" role="dialog" aria-label="ai provider settings">
    <div class="dialog-header">
      <span>ai provider</span>
      <button onclick={onClose} title="close">×</button>
    </div>

    <div class="dialog-body">
      <label>
        <span class="label">base url</span>
        <input type="text" bind:value={form.baseUrl} placeholder="https://api.openai.com/v1" />
        <span class="hint">можно вставить и полный адрес — /chat/completions отрежется сам</span>
      </label>

      <label>
        <span class="label">api key</span>
        <input type="password" bind:value={form.apiKey} placeholder="sk-…" autocomplete="off" />
        <span class="hint">хранится только в этом браузере (localStorage), в паст не попадает</span>
      </label>

      <label>
        <span class="label">model</span>
        <input type="text" bind:value={form.model} placeholder="gpt-4o-mini" />
      </label>

      <div class="row">
        <label class="half">
          <span class="label">temperature</span>
          <input type="number" min="0" max="2" step="0.1" bind:value={form.temperature} />
        </label>
        <label class="half">
          <span class="label">max tokens</span>
          <input
            type="number"
            min="64"
            max="131072"
            step="64"
            bind:value={form.maxTokens}
            disabled={form.autoMaxTokens}
          />
          <label class="check">
            <input type="checkbox" bind:checked={form.autoMaxTokens} />
            <span>auto — на усмотрение провайдера</span>
          </label>
        </label>
      </div>

      <label>
        <span class="label">system prompt</span>
        <textarea rows="4" bind:value={form.systemPrompt}></textarea>
      </label>

      <label class="check">
        <input type="checkbox" bind:checked={form.useTools} />
        <span>tools — модель может читать/писать файлы и выполнять js в превью</span>
      </label>

      {#if serverProvider?.enabled}
        <div class="server-note">
          серверный провайдер доступен: <code>{serverProvider.model}</code><br />
          используется, если поля выше пустые (ключ хранится на сервере)
        </div>
      {/if}

      {#if directResult}<div class="diag">{directResult}</div>{/if}
      {#if serverResult}<div class="diag">{serverResult}</div>{/if}

      {#if checkResult}<div class="ok">{checkResult}</div>{/if}
      {#if checkError}<div class="err">{checkError}</div>{/if}
    </div>

    <div class="dialog-footer">
      <button onclick={check} disabled={checking}>{checking ? "checking…" : "check"}</button>
      <button onclick={diagnose} disabled={diagnosing}>{diagnosing ? "…" : "diagnose"}</button>
      <span class="spacer"></span>
      <button onclick={onClose}>cancel</button>
      <button class="primary" onclick={save}>save</button>
    </div>
  </div>
</div>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
  }
  .dialog {
    width: min(520px, calc(100vw - 32px));
    max-height: calc(100vh - 64px);
    display: flex;
    flex-direction: column;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 6px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  }
  .dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 10px;
    border-bottom: 1px solid var(--border);
  }
  .dialog-header button { padding: 1px 6px; }
  .dialog-body {
    padding: 12px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  label { display: flex; flex-direction: column; gap: 3px; }
  .row { display: flex; gap: 10px; }
  .half { flex: 1; }
  .label { color: var(--fg-dim); }
  .hint { color: var(--fg-dim); opacity: 0.8; }
  .check {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
    color: var(--fg-dim);
  }
  .check input { width: auto; }
  input, textarea {
    background: var(--bg);
    color: var(--fg);
    border: 1px solid var(--border);
    border-radius: 3px;
    padding: 4px 8px;
    font-family: var(--font);
    font-size: var(--size);
    outline: none;
    width: 100%;
  }
  input:focus, textarea:focus { border-color: var(--accent); }
  textarea { resize: vertical; }
  .ok { color: var(--accent); }
  .server-note {
    color: var(--fg-dim);
    border: 1px dashed var(--border);
    border-radius: 3px;
    padding: 6px 8px;
  }
  .server-note code { color: var(--fg); }
  .diag {
    color: var(--fg);
    border-left: 2px solid var(--accent);
    padding: 4px 8px;
    background: var(--bg);
    word-break: break-word;
  }
  .err { color: var(--danger); word-break: break-word; }
  .dialog-footer {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 8px 12px;
    border-top: 1px solid var(--border);
  }
  .spacer { flex: 1; }
</style>